/**
 * Origin-Destination flow map — render curved arcs between OD pairs.
 *
 * Two input modes:
 *
 *   1. **Paste CSV** — origin_lat / origin_lng / dest_lat / dest_lng /
 *      magnitude (column aliases auto-detected). Each row = one flow.
 *
 *   2. **From layer** — pick an uploaded point layer; each feature is
 *      one flow. Either the layer's geometry is the origin (and dest
 *      lat/lng come from attributes), the geometry is the destination
 *      (and origin lat/lng come from attributes), or both endpoints come
 *      from attribute columns. Magnitude is an optional numeric column.
 *
 * Arcs are drawn as great-circle lines. Width scales linearly between
 * MIN_WIDTH and MAX_WIDTH across the magnitude range.
 */
import * as turf from '@turf/turf';
import maplibregl from 'maplibre-gl';
import { setLegend, hideLegend } from '../ui/legend.js';
import { toast } from '../ui/toast.js';

const ALIASES = {
  oLat: ['origin_lat', 'o_lat', 'from_lat', 'orig_lat', 'olat'],
  oLng: ['origin_lng', 'o_lng', 'from_lng', 'origin_lon', 'from_lon', 'orig_lng', 'olng', 'olon'],
  dLat: ['dest_lat', 'd_lat', 'to_lat', 'destination_lat', 'dlat'],
  dLng: ['dest_lng', 'd_lng', 'to_lng', 'destination_lng', 'destination_lon', 'dest_lon', 'to_lon', 'dlng', 'dlon'],
  mag:  ['magnitude', 'weight', 'count', 'flow', 'volume', 'value', 'flows', 'trips'],
  oName: ['origin_name', 'o_name', 'from_name', 'origin', 'olabel'],
  dName: ['dest_name', 'd_name', 'to_name', 'destination', 'dlabel'],
};

// Endpoint dots vs flow lines: separate palettes so the eye can read each.
//   • Origin/destination dots stay on the brand teal/green axis.
//   • Flow arcs use an amber → deep-red ramp that doesn't collide with
//     either endpoint hue and reads well against the basemap.
const ORIGIN_COLOR = '#04648d';   // teal
const DEST_COLOR   = '#98c262';   // green
const FLOW_RAMP    = ['#fde2c4', '#f7b96f', '#e8852f', '#c5571a', '#7a290c'];
const FLOW_WIDTHS  = [1.4, 2.4, 3.6, 5.2, 7.5];

let mapRef = null;
let userLayersRef = null;
export function registerMap(m) { mapRef = m; }
export function registerUserLayers(m) { userLayersRef = m; }

// ---------------------------------------------------------------------------
// Public actions
// ---------------------------------------------------------------------------

export function openOdFlowModal() {
  if (!mapRef) return;
  document.querySelector('#odFlowModal')?.remove();

  const visible = userLayersRef?.getVisibleVectorLayersWithFeatures?.() || [];
  const points  = visible.filter(l => /Point/i.test(l.geometryType || ''));

  document.body.insertAdjacentHTML('beforeend', shellHtml(points));
  const root = document.getElementById('odFlowModal');

  // ---- Tab switching ------------------------------------------------------
  let mode = points.length > 0 ? 'layer' : 'csv';
  const showMode = (m) => {
    mode = m;
    root.querySelectorAll('[data-od-tab]').forEach(b =>
      b.classList.toggle('on', b.dataset.odTab === m));
    root.querySelector('#odCsvPane').style.display   = m === 'csv'   ? 'block' : 'none';
    root.querySelector('#odLayerPane').style.display = m === 'layer' ? 'block' : 'none';
    refreshPreview();
  };
  root.querySelectorAll('[data-od-tab]').forEach(b =>
    b.addEventListener('click', () => showMode(b.dataset.odTab)));

  // ---- CSV mode bindings --------------------------------------------------
  const ta = root.querySelector('#odCsv');
  ta.addEventListener('input', refreshPreview);

  // ---- Layer mode bindings ------------------------------------------------
  const layerSel = root.querySelector('#odLayerSel');
  if (layerSel && points.length > 0) {
    layerSel.addEventListener('change', () => { rebuildLayerColumnPickers(root, points); refreshPreview(); });
    rebuildLayerColumnPickers(root, points);
    root.querySelectorAll('#odLayerPane select, #odLayerPane input').forEach(el =>
      el.addEventListener('change', refreshPreview));
  }

  function refreshPreview() {
    const result = mode === 'csv'
      ? parseCsv(ta.value)
      : buildFromLayer(root, points);
    root.querySelector('#odPreview').innerHTML = previewHtml(result);
    root.querySelector('#odApply').disabled = result.flows.length === 0;
  }

  // ---- Buttons + escape ---------------------------------------------------
  root.addEventListener('click', e => {
    if (e.target === root) root.remove();
    if (e.target.closest('[data-od="cancel"]')) root.remove();
    if (e.target.closest('[data-od="apply"]')) {
      const result = mode === 'csv'
        ? parseCsv(ta.value)
        : buildFromLayer(root, points);
      if (result.flows.length === 0) return;
      apply(result.flows);
      root.remove();
    }
  });
  document.addEventListener('keydown', escClose, { once: true });
  function escClose(e) { if (e.key === 'Escape' && document.getElementById('odFlowModal')) root.remove(); }

  // Initial render
  showMode(mode);
}

export function clearOdFlow() {
  if (!mapRef) return;
  mapRef.getSource('od-flow')?.setData({ type: 'FeatureCollection', features: [] });
  mapRef.getSource('od-flow-points')?.setData({ type: 'FeatureCollection', features: [] });
  hideLegend();
  toast('OD flows cleared');
}

// ---------------------------------------------------------------------------
// CSV parsing
// ---------------------------------------------------------------------------

function parseCsv(raw) {
  const lines = (raw || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  if (lines.length < 2) return { flows: [], errors: ['Need at least a header row and one data row.'] };

  const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
  const idx = {
    oLat:  headers.findIndex(h => ALIASES.oLat.includes(h)),
    oLng:  headers.findIndex(h => ALIASES.oLng.includes(h)),
    dLat:  headers.findIndex(h => ALIASES.dLat.includes(h)),
    dLng:  headers.findIndex(h => ALIASES.dLng.includes(h)),
    mag:   headers.findIndex(h => ALIASES.mag.includes(h)),
    oName: headers.findIndex(h => ALIASES.oName.includes(h)),
    dName: headers.findIndex(h => ALIASES.dName.includes(h)),
  };

  const errors = [];
  if (idx.oLat < 0) errors.push('Missing origin_lat column.');
  if (idx.oLng < 0) errors.push('Missing origin_lng column.');
  if (idx.dLat < 0) errors.push('Missing dest_lat column.');
  if (idx.dLng < 0) errors.push('Missing dest_lng column.');
  if (errors.length) return { flows: [], errors };

  const flows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const oLat = parseFloat(cols[idx.oLat]);
    const oLng = parseFloat(cols[idx.oLng]);
    const dLat = parseFloat(cols[idx.dLat]);
    const dLng = parseFloat(cols[idx.dLng]);
    if ([oLat, oLng, dLat, dLng].some(v => Number.isNaN(v))) continue;
    const mag = idx.mag >= 0 ? Number(cols[idx.mag]) : 1;
    flows.push({
      origin: [oLng, oLat],
      dest:   [dLng, dLat],
      magnitude: Number.isFinite(mag) ? mag : 1,
      originName: idx.oName >= 0 ? cols[idx.oName] : null,
      destName:   idx.dName >= 0 ? cols[idx.dName] : null,
    });
  }
  return { flows, errors };
}

function parseCsvLine(line) {
  const out = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (c === ',' && !inQ) {
      out.push(cur.trim()); cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur.trim());
  return out;
}

// ---------------------------------------------------------------------------
// From-layer parsing
// ---------------------------------------------------------------------------

function buildFromLayer(root, points) {
  const layerId = +root.querySelector('#odLayerSel')?.value;
  const layer = points.find(p => p.id === layerId);
  if (!layer) return { flows: [], errors: ['Pick a layer.'] };

  const geomRole = root.querySelector('#odGeomRole').value;   // 'origin' | 'dest' | 'none'
  const oLatCol  = root.querySelector('#odOLat')?.value || '';
  const oLngCol  = root.querySelector('#odOLng')?.value || '';
  const dLatCol  = root.querySelector('#odDLat')?.value || '';
  const dLngCol  = root.querySelector('#odDLng')?.value || '';
  const magCol   = root.querySelector('#odMag')?.value  || '';
  const oNameCol = root.querySelector('#odOName')?.value || '';
  const dNameCol = root.querySelector('#odDName')?.value || '';

  const errors = [];
  if (geomRole !== 'origin' && (!oLatCol || !oLngCol)) errors.push('Pick origin lat/lng columns.');
  if (geomRole !== 'dest'   && (!dLatCol || !dLngCol)) errors.push('Pick destination lat/lng columns.');
  if (errors.length) return { flows: [], errors };

  const flows = [];
  for (const f of layer.features) {
    if (f.geometry?.type !== 'Point') continue;
    const geom = f.geometry.coordinates;
    const props = f.properties || {};

    const origin = geomRole === 'origin'
      ? geom
      : [parseFloat(props[oLngCol]), parseFloat(props[oLatCol])];
    const dest = geomRole === 'dest'
      ? geom
      : [parseFloat(props[dLngCol]), parseFloat(props[dLatCol])];

    if (origin.some(v => !Number.isFinite(v))) continue;
    if (dest.some(v => !Number.isFinite(v))) continue;
    if (origin[0] === dest[0] && origin[1] === dest[1]) continue;

    const mag = magCol ? Number(props[magCol]) : 1;
    flows.push({
      origin, dest,
      magnitude: Number.isFinite(mag) ? mag : 1,
      originName: oNameCol ? props[oNameCol] : null,
      destName:   dNameCol ? props[dNameCol] : null,
    });
  }
  return { flows, errors: [] };
}

function rebuildLayerColumnPickers(root, points) {
  const layerId = +root.querySelector('#odLayerSel')?.value;
  const layer = points.find(p => p.id === layerId);
  if (!layer) return;

  const sample = layer.features[0]?.properties || {};
  const cols = Object.keys(sample).filter(k => k !== '_fid' && k !== 'geometry');
  const numericCols = cols.filter(c => layer.features.slice(0, 10).some(f => {
    const v = f.properties?.[c];
    return v != null && v !== '' && !Number.isNaN(Number(v));
  }));

  // Auto-pick by alias
  const find = (aliases) => cols.find(c => aliases.includes(c.toLowerCase())) || '';
  const defaults = {
    oLat:  find(ALIASES.oLat),
    oLng:  find(ALIASES.oLng),
    dLat:  find(ALIASES.dLat),
    dLng:  find(ALIASES.dLng),
    mag:   numericCols.find(c => ALIASES.mag.includes(c.toLowerCase())) || '',
    oName: find(ALIASES.oName),
    dName: find(ALIASES.dName),
  };

  const opts = (selected) => `<option value="">(none)</option>` + cols.map(c =>
    `<option value="${escape(c)}" ${c === selected ? 'selected' : ''}>${escape(c)}</option>`
  ).join('');

  const numOpts = (selected) => `<option value="">(none)</option>` + numericCols.map(c =>
    `<option value="${escape(c)}" ${c === selected ? 'selected' : ''}>${escape(c)}</option>`
  ).join('');

  root.querySelector('#odOLat').innerHTML  = opts(defaults.oLat);
  root.querySelector('#odOLng').innerHTML  = opts(defaults.oLng);
  root.querySelector('#odDLat').innerHTML  = opts(defaults.dLat);
  root.querySelector('#odDLng').innerHTML  = opts(defaults.dLng);
  root.querySelector('#odMag').innerHTML   = numOpts(defaults.mag);
  root.querySelector('#odOName').innerHTML = opts(defaults.oName);
  root.querySelector('#odDName').innerHTML = opts(defaults.dName);

  // Choose a sensible geometry role: if neither origin nor dest cols
  // were detected, treat the geometry as origin (most common pattern).
  const sel = root.querySelector('#odGeomRole');
  if (sel && !defaults.oLat && !defaults.oLng && (defaults.dLat || defaults.dLng)) sel.value = 'origin';
  else if (sel && !defaults.dLat && !defaults.dLng && (defaults.oLat || defaults.oLng)) sel.value = 'dest';

  // Disable the column pickers that get overridden by geometry
  applyGeomRole(root);
  sel?.addEventListener('change', () => applyGeomRole(root), { once: true });
}

function applyGeomRole(root) {
  const role = root.querySelector('#odGeomRole')?.value;
  const oCols = [root.querySelector('#odOLat'), root.querySelector('#odOLng')];
  const dCols = [root.querySelector('#odDLat'), root.querySelector('#odDLng')];
  oCols.forEach(el => el && (el.disabled = role === 'origin'));
  dCols.forEach(el => el && (el.disabled = role === 'dest'));
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

function apply(flows) {
  if (!mapRef) return;

  const mags = flows.map(f => f.magnitude);
  const minMag = Math.min(...mags);
  const maxMag = Math.max(...mags);

  // Equal-interval bins — same step width across the full magnitude range.
  // For uniform values (min == max) collapse to a single bin so we don't
  // divide by zero.
  const bins = computeEqualIntervalBins(minMag, maxMag, FLOW_RAMP.length);
  const binFor = (m) => {
    if (bins.breaks.length === 0) return 0;
    for (let i = 0; i < bins.breaks.length; i++) {
      if (m < bins.breaks[i]) return i;
    }
    return bins.breaks.length;   // last bin
  };

  const arcs = flows.map(f => {
    const idx = binFor(f.magnitude);
    return turf.greatCircle(turf.point(f.origin), turf.point(f.dest), {
      npoints: 64,
      properties: {
        magnitude: f.magnitude,
        bin:       idx,
        color:     FLOW_RAMP[idx],
        width:     FLOW_WIDTHS[idx],
        origin_name: f.originName,
        dest_name:   f.destName,
      },
    });
  });

  const points = [];
  const seen = new Set();
  for (const f of flows) {
    const ok = `o:${f.origin.join(',')}`;
    if (!seen.has(ok)) {
      seen.add(ok);
      points.push({ type: 'Feature', geometry: { type: 'Point', coordinates: f.origin },
                    properties: { kind: 'origin', name: f.originName } });
    }
    const dk = `d:${f.dest.join(',')}`;
    if (!seen.has(dk)) {
      seen.add(dk);
      points.push({ type: 'Feature', geometry: { type: 'Point', coordinates: f.dest },
                    properties: { kind: 'dest', name: f.destName } });
    }
  }

  mapRef.getSource('od-flow').setData({ type: 'FeatureCollection', features: arcs });
  mapRef.getSource('od-flow-points').setData({ type: 'FeatureCollection', features: points });

  const bounds = new maplibregl.LngLatBounds();
  for (const p of points) bounds.extend(p.geometry.coordinates);
  if (!bounds.isEmpty()) mapRef.fitBounds(bounds, { padding: 80, maxZoom: 11, duration: 1000 });

  pushOdLegend(flows.length, bins);
  toast(`${flows.length} OD flow${flows.length === 1 ? '' : 's'} rendered · ${bins.usedBins} bin${bins.usedBins === 1 ? '' : 's'}`);
}

/**
 * Build equal-interval breaks across [min, max] for `n` bins.
 * Returns `{ breaks, edges, usedBins }`:
 *   breaks  — n-1 inner break points
 *   edges   — n+1 boundaries (= [min, ...breaks, max])
 *   usedBins — number of bins actually populated (1 when min == max)
 */
function computeEqualIntervalBins(min, max, n) {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return { breaks: [], edges: [min, max], usedBins: 1 };
  }
  const step = (max - min) / n;
  const breaks = Array.from({ length: n - 1 }, (_, i) => min + step * (i + 1));
  return { breaks, edges: [min, ...breaks, max], usedBins: n };
}

function pushOdLegend(count, bins) {
  const fmt = (n) => !Number.isFinite(n) ? '—'
    : Number.isInteger(n) ? n.toLocaleString()
    : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

  const items = [
    { color: ORIGIN_COLOR, label: 'Origin' },
    { color: DEST_COLOR,   label: 'Destination' },
  ];

  // One swatch per populated bin, mid-low to mid-high.
  const usedColors = FLOW_RAMP.slice(0, bins.usedBins);
  for (let i = 0; i < usedColors.length; i++) {
    const lo = bins.edges[i];
    const hi = bins.edges[i + 1];
    const label = bins.usedBins === 1
      ? `magnitude ${fmt(lo)}`
      : `${fmt(lo)} – ${fmt(hi)}`;
    items.push({ color: usedColors[i], label });
  }

  setLegend(`OD flows · ${count}`, items);
}

// ---------------------------------------------------------------------------
// Modal HTML
// ---------------------------------------------------------------------------

function shellHtml(points) {
  const sample = `origin_name,origin_lat,origin_lng,dest_name,dest_lat,dest_lng,magnitude
Accra,5.614,-0.186,Kumasi,6.694,-1.622,420
Accra,5.614,-0.186,Tamale,9.407,-0.839,180
Kumasi,6.694,-1.622,Cape Coast,5.104,-1.255,95`;

  const layerOptions = points.map(p =>
    `<option value="${p.id}">${escape(p.name)}</option>`
  ).join('');

  return `
    <div class="modal-bg show" id="odFlowModal">
      <div class="modal" style="width:600px;max-width:96vw">
        <h3 style="margin:0">Origin-Destination flows</h3>
        <p style="font-size:11.5px;color:var(--text3);margin:6px 0 12px;line-height:1.5">
          Render great-circle arcs between origins and destinations. Width scales with magnitude.
        </p>

        <div class="od-tabs">
          <button class="od-tab" data-od-tab="layer" ${points.length === 0 ? 'disabled title="No visible point layers"' : ''}>From layer</button>
          <button class="od-tab" data-od-tab="csv">Paste CSV</button>
        </div>

        <div id="odLayerPane" style="display:none">
          <div style="display:flex;flex-direction:column;gap:10px;margin-top:12px">
            <label style="font-size:11px;color:var(--text2)">
              Point layer
              <select id="odLayerSel" style="width:100%;margin-top:4px;padding:8px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg3);color:var(--text);font-size:13px">
                ${layerOptions}
              </select>
            </label>
            <label style="font-size:11px;color:var(--text2)">
              Each feature's geometry is the
              <select id="odGeomRole" style="width:100%;margin-top:4px;padding:8px 10px;border-radius:6px;border:1px solid var(--border);background:var(--bg3);color:var(--text);font-size:13px">
                <option value="origin">origin (destination from columns)</option>
                <option value="dest">destination (origin from columns)</option>
                <option value="none">neither (both endpoints from columns)</option>
              </select>
            </label>
            <div class="od-cols">
              <div><label>Origin lat</label><select id="odOLat"></select></div>
              <div><label>Origin lng</label><select id="odOLng"></select></div>
              <div><label>Dest lat</label><select id="odDLat"></select></div>
              <div><label>Dest lng</label><select id="odDLng"></select></div>
              <div><label>Magnitude</label><select id="odMag"></select></div>
              <div></div>
              <div><label>Origin label</label><select id="odOName"></select></div>
              <div><label>Dest label</label><select id="odDName"></select></div>
            </div>
          </div>
        </div>

        <div id="odCsvPane" style="display:none">
          <p style="font-size:11px;color:var(--text3);margin:12px 0 6px">
            Header row + one row per flow. Columns: <code>origin_lat, origin_lng, dest_lat, dest_lng, magnitude</code> (aliases like <code>o_lat</code>, <code>weight</code> are recognised too).
          </p>
          <textarea id="odCsv" spellcheck="false"
                    style="width:100%;min-height:140px;background:var(--bg3);border:1px solid var(--border);border-radius:6px;color:var(--text);font-family:var(--mono);font-size:11.5px;padding:10px 12px;line-height:1.5;resize:vertical"
                    placeholder="${escape(sample)}">${escape(sample)}</textarea>
        </div>

        <div id="odPreview" style="margin-top:12px;font-size:11.5px;color:var(--text2);font-family:var(--mono);min-height:18px"></div>

        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:14px">
          <button class="btn" data-od="cancel">Cancel</button>
          <button class="btn btn-primary" id="odApply" data-od="apply">Render flows</button>
        </div>
      </div>

      <style>
        .od-tabs { display:flex; gap:2px; border-bottom:1px solid var(--border); margin-bottom:4px; }
        .od-tab {
          padding:8px 14px; background:none; border:none; cursor:pointer;
          color:var(--text3); font-family:var(--font); font-size:12.5px;
          border-bottom:2px solid transparent; margin-bottom:-1px;
          transition: color .15s var(--ease), border-color .15s var(--ease);
        }
        .od-tab:not(:disabled):hover { color:var(--text2); }
        .od-tab.on { color:var(--text); border-bottom-color:var(--primary); }
        .od-tab:disabled { opacity:0.4; cursor:not-allowed; }
        .od-cols { display:grid; grid-template-columns:1fr 1fr; gap:8px 12px; margin-top:6px; }
        .od-cols label { display:block; font-size:10.5px; color:var(--text3); margin-bottom:3px; text-transform:uppercase; letter-spacing:0.06em; font-weight:600; }
        .od-cols select { width:100%; padding:6px 8px; background:var(--bg3); border:1px solid var(--border); border-radius:5px; color:var(--text); font-size:11.5px; }
        .od-cols select:disabled { opacity:0.45; cursor:not-allowed; }
      </style>
    </div>
  `;
}

function previewHtml(result) {
  if (result.errors?.length) {
    return `<span style="color:var(--red)">${result.errors.join(' ')}</span>`;
  }
  if (!result.flows || result.flows.length === 0) {
    return `<span>No valid flows yet.</span>`;
  }
  const mags = result.flows.map(f => f.magnitude);
  return `${result.flows.length} flow${result.flows.length === 1 ? '' : 's'} · magnitude ${Math.min(...mags).toLocaleString()} – ${Math.max(...mags).toLocaleString()}`;
}

function escape(s) {
  return String(s ?? '').replace(/[&<>"]/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));
}
